import { differenceInCalendarDays } from "date-fns";
import type { GridTask } from "../types";

// "Soon" window before the due date. Drives both the amber-border cell
// treatment and the "Due soon" quick filter — kept tight (3 days) so amber
// signals something actually imminent rather than next-week-ish.
export const DUE_SOON_WINDOW_DAYS = 3;

export type CellState =
  | { kind: "open" }
  | { kind: "due_soon"; dueAt: Date }
  | { kind: "overdue"; daysLate: number; dueAt: Date }
  | { kind: "not_started"; startAt?: Date }
  | { kind: "done"; completedAt?: Date }
  | { kind: "skipped" }
  | { kind: "closed_stale" }
  | { kind: "na" };

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function triggerDate(task: GridTask): Date | null {
  return parseDate(task.startAt) ?? parseDate(task.startOn);
}

function dueDate(task: GridTask): Date | null {
  return parseDate(task.dueAt) ?? parseDate(task.dueOn);
}

/**
 * Derive the dark-cockpit cell state for one (task, hire) pair.
 *
 * Status drives the state directly for terminal and not-yet-started cases:
 *   completed   -> done
 *   skipped     -> skipped
 *   closed_stale-> closed_stale (visually same as skipped, distinct tooltip)
 *   removed     -> na
 *   upcoming    -> not_started (locked glyph; never escalates to overdue)
 *
 * The started statuses (active / in_progress / blocked / failed) run through
 * the pure timing ladder — no trigger-date guard, because a started task is
 * already in flight regardless of what the schedule says:
 *   no due_at                                -> open
 *   now < (due_at - DUE_SOON_WINDOW)         -> open
 *   (due_at - DUE_SOON_WINDOW) <= now < due  -> due_soon
 *   now >= due_at                            -> overdue
 */
export function deriveCellState(task: GridTask | undefined, now: Date): CellState {
  if (!task) return { kind: "na" };

  switch (task.status) {
    case "completed":
      return { kind: "done", completedAt: parseDate(task.statusUpdatedAt) ?? undefined };
    case "skipped":
      return { kind: "skipped" };
    case "closed_stale":
      return { kind: "closed_stale" };
    case "removed":
      return { kind: "na" };
    case "upcoming": {
      const trigger = triggerDate(task);
      return { kind: "not_started", startAt: trigger ?? undefined };
    }
    case "active":
    case "in_progress":
    case "blocked":
    case "failed":
    default:
      break;
  }

  const due = dueDate(task);
  if (!due) return { kind: "open" };

  const dueSoonStartMs = due.getTime() - DUE_SOON_WINDOW_DAYS * 24 * 60 * 60 * 1000;
  if (now.getTime() < dueSoonStartMs) return { kind: "open" };
  if (now.getTime() < due.getTime()) return { kind: "due_soon", dueAt: due };

  const daysLate = Math.max(1, differenceInCalendarDays(now, due));
  return { kind: "overdue", daysLate, dueAt: due };
}
